import React, { forwardRef, RefForwardingComponent } from 'react';

import useTheme from '@gojob/ui-bits/styles/useTheme';

import LiveJobPreview, {
  LiveJobPreviewProps,
} from 'src/components/InfiniteJobList/InfiniteListItem/LiveJobPreview';
import useLiveJobPreviewIndex, {
  VIDEO_PROMOTION_INDEX,
} from 'src/components/InfiniteJobList/InfiniteListItem/useLiveJobPreviewIndex';
import useVideoPromotion from 'src/components/InfiniteJobList/InfiniteListItem/useVideoPromotion';
import VideoPromotion from 'src/components/VideoPromotion';

export type InfiniteListItemProps = Omit<LiveJobPreviewProps, 'ref'>;

const useStyles = () => {
  const { spacing } = useTheme();

  return {
    root: {
      width: '100%',
      height: 'auto',
      boxSizing: 'border-box',
      padding: spacing(1),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  } as const;
};

const InfiniteListItem: RefForwardingComponent<HTMLDivElement, InfiniteListItemProps> = (props, ref) => {
  const { index: originalIndex = 0, className, style, onDisplayChanged, ...other } = props;

  const classes = useStyles();

  const videoPromotion = useVideoPromotion();

  const index = useLiveJobPreviewIndex(originalIndex);

  if (videoPromotion) {
    if (originalIndex === VIDEO_PROMOTION_INDEX) {
      const { ariaLabel, buttonLabel, href, htmlId, posterImage, desktopFiles, mobileFiles } = videoPromotion;

      return (
        <VideoPromotion
          ref={ref}
          css={classes.root}
          id={htmlId}
          aria-label={ariaLabel}
          href={href}
          posterSrc={posterImage}
          desktopFiles={desktopFiles}
          mobileFiles={mobileFiles}
          onPlay={onDisplayChanged}
          onPosterLoaded={onDisplayChanged}
          className={className}
          style={style}
        >
          {buttonLabel}
        </VideoPromotion>
      );
    }

    return (
      <LiveJobPreview
        ref={ref}
        css={classes.root}
        index={index}
        onDisplayChanged={onDisplayChanged}
        className={className}
        style={style}
        {...other}
      />
    );
  }

  return (
    <LiveJobPreview
      ref={ref}
      css={classes.root}
      index={originalIndex}
      onDisplayChanged={onDisplayChanged}
      className={className}
      style={style}
      {...other}
    />
  );
};

export default forwardRef(InfiniteListItem);
